import * as React from 'react'
import { ReactElement } from 'react'
import UserTierForm, { UserTierFormProps } from '../../Form/UserTier/UserTierForm'
import { FormikContextType, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import { UserTierAction } from '../../Form/UserTier/UserTierList'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
} from '@fluentui/react-components'
import { DismissRegular, SaveRegular } from '@fluentui/react-icons'
import { DeleteRegular } from '@fluentui/react-icons'
import { BaseTierDto } from '../../../config/finance/financeConfig'
import { UserDto } from '../../../config/userConfig'
import { useTranslation } from 'react-i18next'

export type UserTierDialogProps<T extends BaseTierDto> = {
    show: boolean
    setOpen: (open: boolean) => void
    initialValues: T
    index: number
    action: UserTierAction
    createTierFormContainer: (
        props: UserTierFormProps<T>,
        fileContext: FormikContextType<FileMutationDto>,
        selectableUsers: UserDto[],
    ) => React.ReactElement
}

export default function UserTierDialog<T extends BaseTierDto>(props: UserTierDialogProps<T>): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue } = useFormikContext<FileMutationDto>()

    const removeItem = (cutIndex: number) => {
        values.tiers = values.tiers.filter((_, index) => index !== cutIndex)
        setFieldValue('tiers', values.tiers)
    }

    const resetItem = (cutIndex: number) => {
        if (props.action === UserTierAction.ADD) {
            values.tiers = values.tiers.filter((_, index) => index !== cutIndex)
            setFieldValue('tiers', values.tiers)
        } else {
            values.tiers[props.index] = props.initialValues
            setFieldValue(`tiers[${props.index}]`, props.initialValues)
        }
    }

    const onDismiss = () => {
        resetItem(props.index)
        props.setOpen(false)
    }
    const onAdd = () => {
        if (!values.tiers[props.index].userId) {
            removeItem(props.index)
        }
        props.setOpen(false)
    }

    return (
        <Dialog open={props.show} onOpenChange={onDismiss}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {props.action === UserTierAction.EDIT
                            ? translate('file.edit.form.finance.rates.tier.edit')
                            : translate('file.edit.form.finance.rates.tier.add')}
                    </DialogTitle>
                    <DialogContent>
                        <UserTierForm<T> {...props} onSubmit={onAdd} />
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissRegular />} onClick={onDismiss}>
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button icon={<SaveRegular />} appearance="primary" onClick={onAdd}>
                            {translate('action.save')}
                        </Button>
                        {props.action === UserTierAction.EDIT && (
                            <Button
                                appearance="transparent"
                                icon={<DeleteRegular />}
                                onClick={() => {
                                    removeItem(props.index)
                                    props.setOpen(false)
                                }}
                            >
                                {translate('action.delete')}
                            </Button>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
